import { cva, cx } from '@vizit/theme';
import type * as React from 'react';
import { ToggleButton, ToggleButtonGroup, type ToggleButtonGroupProps } from 'react-aria-components';

interface SegmentedControlOption<K extends string> {
  /**
   * The key reported through `onSelectionChange` when this segment is picked
   */
  id: K;
  /**
   * The visible content of the segment
   */
  label: React.ReactNode;
  /**
   * Optional count shown after the label, e.g. how many items the view holds
   */
  count?: number;
}

interface SegmentedControlProps<K extends string>
  extends Omit<
    ToggleButtonGroupProps,
    | 'selectionMode'
    | 'selectedKeys'
    | 'defaultSelectedKeys'
    | 'onSelectionChange'
    | 'disallowEmptySelection'
    | 'className'
  > {
  /**
   * Additional CSS classes for the group. A plain string: the render-function form is not supported.
   */
  className?: string;
  /**
   * The segments, in display order
   */
  options: SegmentedControlOption<K>[];
  /**
   * The key of the selected segment
   */
  selectedKey: K;
  /**
   * Called with the newly picked key. Pressing the selected segment again does not call it.
   */
  onSelectionChange: (key: K) => void;
  /**
   * Accessible name for the group; required because the segments alone do not say what they switch
   */
  'aria-label': string;
}

const groupClasses = cva({
  base: ['inline-flex items-center gap-2 p-1', 'rounded-none border border-secondary bg-secondary-alt'],
});

const segmentClasses = cva({
  base: [
    'flex h-14 cursor-pointer items-center justify-center gap-3 px-5 outline-none',
    'rounded-none border border-transparent text-sm font-semibold text-quaternary',
    'transition-all duration-200',
    'hover:text-secondary',
    'data-[selected]:border-secondary data-[selected]:bg-white data-[selected]:text-secondary data-[selected]:drop-shadow-sm',
    'data-[focus-visible]:ring-2 data-[focus-visible]:ring-brand-600 data-[focus-visible]:ring-offset-1',
    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
  ],
});

/**
 * A row of mutually exclusive segments for switching between sibling views,
 * such as Collections and Benchmarks. Exactly one segment is always selected.
 *
 * Built on React Aria's ToggleButtonGroup rather than Tabs because it only
 * switches a value: the views it controls can each render their own toolbar
 * around it, which a single TabList cannot be split across.
 *
 * @example
 * <SegmentedControl aria-label="Library view" options={[{ id: 'a', label: 'A' }, { id: 'b', label: 'B' }]} selectedKey="a" onSelectionChange={setView} />
 */
function SegmentedControl<K extends string>(props: SegmentedControlProps<K>) {
  const { options, selectedKey, onSelectionChange, className, ...other } = props;

  return (
    <ToggleButtonGroup
      {...other}
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[selectedKey]}
      onSelectionChange={(keys) => {
        const [next] = keys;
        if (next !== undefined && next !== selectedKey) {
          onSelectionChange(next as K);
        }
      }}
      className={cx(groupClasses(), className)}
    >
      {options.map((option) => (
        <ToggleButton key={option.id} id={option.id} className={segmentClasses()}>
          {option.label}
          {option.count !== undefined && (
            <span className="text-xs font-regular text-quaternary tabular-nums">{option.count}</span>
          )}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

export { SegmentedControl };
export type { SegmentedControlOption, SegmentedControlProps };
